/* eslint-disable camelcase */
import cartService from '@/services/cart.service'
import EventTracking from '@/helpers/event-tracking'

export default {
  namespaced: true,

  state: {
    order_type: '',
    source: '',
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    address: '',
    city: '',
    state_abbrev: '',
    zipcode: '',
    reference_no: '',
    requested_payment_method: '',
    order_items_attributes: [],
    checkoutStatus: null,
    checkoutErrors: [],
    ordered_items: [],
    payment_source: '',
    payment_reference_id: null,
    payment_amount: null,
    payment_prepaid: false
  },

  mutations: {
    addItemToCart (state, { product, item, quantity }) {
      const cartItem = state.order_items_attributes.find(
        orderItem => orderItem.product_id === item.id
      )

      if (cartItem) {
        cartItem.quantity += quantity
      } else {
        state.order_items_attributes.push({
          product_parent_id: product.id,
          product_id: item.id,
          product_name: item.name,
          product_brand: product.brand,
          product_strain: product.strain,
          weight: `${+item.unit}${item.unit_type}`,
          price: item.price_cents,
          quantity: quantity
        })
      }
    },

    setCartState (state, cart) {
      Object.assign(state, { ...cart })
    },

    updateOrderedItems (state) {
      state.ordered_items = state.order_items_attributes
    },

    emptyCart (state) {
      state.order_items_attributes = []
    },

    emptyPersonalInfo (state) {
      state.first_name = ''
      state.last_name = ''
      state.email = ''
      state.telephone = ''
    },

    removeItemFromCart (state, id) {
      const index = state.order_items_attributes
        .map(item => item.product_id)
        .indexOf(id)

      if (index >= 0) {
        state.order_items_attributes.splice(index, 1)
      }
    },

    incrementItemQuantity (state, id) {
      const cartItem = state.order_items_attributes.find(
        product => product.id === id
      )
      cartItem.quantity++
    },

    decrementItemQuantity (state, id) {
      const cartItem = state.order_items_attributes.find(
        product => product.id === id
      )
      cartItem.quantity--
    },

    setItemQuantity (state, { product_id, quantity }) {
      const cartItem = state.order_items_attributes.find(
        product => product.product_id === product_id
      )
      cartItem.quantity = quantity
    },

    setCheckoutStatus (state, status) {
      state.checkoutStatus = status
    },

    setCheckoutErrors (state, errors) {
      state.checkoutErrors = errors
    },

    setFirstName (state, name) {
      state.first_name = name
    },

    setLastName (state, name) {
      state.last_name = name
    },

    setEmail (state, email) {
      state.email = email
    },

    setTelephone (state, telephone) {
      state.telephone = telephone
    },
    setOrderType (state, order_type) {
      state.order_type = order_type
    },
    setaddress (state, address) {
      state.address = address
    },
    setCity (state, city) {
      state.city = city
    },
    setStateAbbrev (state, state_abbrev) {
      state.state_abbrev = state_abbrev
    },
    setZipcode (state, zipcode) {
      state.zipcode = zipcode
    },
    setReferenceNo (state, reference_no) {
      state.reference_no = reference_no
    },
    setPaymentMethod (state, payment_method) {
      state.requested_payment_method = payment_method
    },
    setSource (state, source) {
      state.source = source
    },
    setPaymentSource (state, payment_source) {
      state.payment_source = payment_source
    },
    setPaymentPrepaid (state, payment_prepaid) {
      state.payment_prepaid = payment_prepaid
    },
    setPaymentReferenceId (state, payment_reference_id) {
      state.payment_reference_id = payment_reference_id
    },
    setPaymentAmount (state, payment_amount) {
      state.payment_amount = payment_amount
    }
  },

  actions: {
    loadCart ({ commit }) {
      const savedCart = cartService.loadCart()
      if (savedCart) {
        commit('setCartState', savedCart)
      }
    },

    saveCart ({ state }) {
      cartService.storeCart({
        order_items_attributes: state.order_items_attributes
      })
    },

    resetCart ({ commit }) {
      commit('emptyPersonalInfo')
      commit('emptyCart')
      commit('setCheckoutStatus', null)
    },

    placeOrder ({ state, commit, dispatch }) {
      commit('setCheckoutErrors', [])
      commit('setCheckoutStatus', 'processing')

      cartService
        .placeOrder(state)
        .then(response => {
          commit('setCheckoutStatus', 'success')
          commit('updateOrderedItems')
          commit('emptyCart')
          commit('emptyPersonalInfo')
          dispatch('orderSuccessEvent', response)
          cartService.deleteStoredCart()
        })
        .catch(error => {
          const orderError = new CustomEvent('posabit-order-error', {
            detail: { error: error }
          })
          document.dispatchEvent(orderError)

          if (error.response) {
            console.log('Problem With Order Response ', error.response)
            commit('setCheckoutStatus', `error: ${error.response.statusText}`)
            commit(
              'setCheckoutErrors',
              JSON.parse(error.response.data.output).errors
            )
          } else if (error.request) {
            console.log('Problem With Order Request!', JSON.stringify(error))
            commit('setCheckoutStatus', `error: Problem With Order Request.`)
            commit('setCheckoutErrors', [
              'Unable to submit your order at this time. Please wait then try again.'
            ])
          } else {
            console.log('Unknown Order Error', error)
            commit('setCheckoutStatus', `error: ${error.message}`)
            commit('setCheckoutErrors', [error.message])
          }
        })
    },

    orderSuccessEvent ({ rootGetters }, response) {
      let total = 0
      const orderData = response.data
      const trackProducts = orderData.order_items.map(product => {
        const parentProduct = rootGetters['products/findProductByVariant'](
          product.product_id
        )
        total += +product.price * +product.quantity
        return {
          id: product.product_id,
          name: product.product_name,
          brand: product.product_brand,
          category: parentProduct.category,
          price: product.price,
          quantity: product.quantity
        }
      })
      const trackOrder = orderData.incoming_order
      trackOrder.total = total

      EventTracking.completeOrder(trackOrder, trackProducts)
      EventTracking.sendPageview()
    },

    addItemToCart ({ commit, dispatch }, { product, item, quantity }) {
      commit('addItemToCart', { product, item, quantity })
      commit('setCheckoutStatus', null)

      const productTrack = {
        id: item.id,
        name: item.name,
        brand: product.brand,
        category: product.category,
        price: item.price_cents,
        quantity: quantity
      }

      EventTracking.addToCart(productTrack)

      const addToCart = new CustomEvent('posabit-add-to-cart', {
        detail: { product: product, item: item, quantity: quantity }
      })
      document.dispatchEvent(addToCart)

      dispatch('saveCart')
    },
    removeItemFromCart (
      { commit, dispatch, state, rootGetters },
      { id, parent_id }
    ) {
      const product = rootGetters['products/findProduct'](parent_id)
      let item

      if (!product) {
        // product no longer exists so we're quietly purging it
        commit('removeItemFromCart', id)
        commit('setCheckoutStatus', null)
        dispatch('saveCart')
        return
      }

      item = product.prices.find(price => price.id === id)
      const productTrack = {
        id: item.id,
        name: item.name,
        brand: product.brand,
        category: product.category,
        price: item.price_cents,
        quantity: item.quantity
      }

      EventTracking.removeFromCart(productTrack)

      commit('removeItemFromCart', id)
      commit('setCheckoutStatus', null)

      if (product && item) {
        const addToCart = new CustomEvent('posabit-remove-from-cart', {
          detail: { product: product, item: item }
        })
        document.dispatchEvent(addToCart)
      }

      dispatch('saveCart')
    },
    updateItemQuantity ({ commit, dispatch }, { product_id, quantity }) {
      commit('setItemQuantity', { product_id: product_id, quantity: quantity })
      dispatch('saveCart')
    },
    setOrderType ({ commit }, order_type) {
      commit('setOrderType', order_type)
    },
    setFirstName ({ commit }, name) {
      commit('setFirstName', name)
    },
    setLastName ({ commit }, name) {
      commit('setLastName', name)
    },
    setEmail ({ commit }, email) {
      commit('setEmail', email)
    },
    setTelephone ({ commit }, telephone) {
      commit('setTelephone', telephone)
    },
    setaddress ({ commit }, address) {
      commit('setaddress', address)
    },
    setCity ({ commit }, city) {
      commit('setCity', city)
    },
    setStateAbbrev ({ commit }, state_abbrev) {
      commit('setStateAbbrev', state_abbrev)
    },
    setZipcode ({ commit }, zipcode) {
      commit('setZipcode', zipcode)
    },
    setReferenceNo ({ commit }, reference_no) {
      commit('setReferenceNo', reference_no)
    },
    setPaymentMethod ({ commit }, payment_method) {
      commit('setPaymentMethod', payment_method)
    },
    setSource ({ commit }, source) {
      commit('setSource', source)
    },
    setPaymentSource ({ commit }, payment_source) {
      commit('setPaymentSource', payment_source)
    },
    setPaymentPrepaid ({ commit }, payment_prepaid) {
      commit('setPaymentPrepaid', payment_prepaid)
    },
    setPaymentReferenceId ({ commit }, payment_reference_id) {
      commit('setPaymentReferenceId', payment_reference_id)
    },
    setPaymentAmount ({ commit }, payment_amount) {
      commit('setPaymentAmount', payment_amount)
    }
  },

  getters: {
    getCartCount: state => {
      return state.order_items_attributes.reduce((prev, current) => {
        prev += current.quantity
        return prev
      }, 0)
    }
  }
}
